import { Link, useParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import SeoHead from '@/components/feature/SeoHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import CountdownTimer from '@/components/feature/CountdownTimer';
import ReminderButton from '@/components/feature/ReminderButton';
import { liveSessions } from '@/mocks/lives';
import { formatCount, statusText, toNextTimestamp } from '@/utils/time';

const pad = (value: number) => String(value).padStart(2, '0');

const buildIso = (time: string): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = pad(now.getMonth() + 1);
  const day = pad(now.getDate());
  return `${year}-${month}-${day}T${time}:00+08:00`;
};

export default function LiveDetailPage() {
  const { id } = useParams<{ id: string }>();
  const session = liveSessions.find((item) => item.id === id);

  if (!session) {
    return (
      <SiteLayout>
        <div className="container-page flex min-h-[60vh] flex-col items-center justify-center py-20 text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-2xl text-foreground-400">
            <i className="ri-live-line" aria-hidden="true" />
          </span>
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">没有找到这场直播</h1>
          <p className="mt-2 max-w-md text-sm text-foreground-600">
            场次可能已经下架或链接有误，可以回到今日排期页查看正在直播与即将开始的场次。
          </p>
          <Link
            to="/schedule"
            className="mt-6 flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            返回今日排期
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line" aria-hidden="true" />
            </span>
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const sorted = [...liveSessions].sort((a, b) => a.start.localeCompare(b.start));
  const index = sorted.findIndex((item) => item.id === session.id);
  const prev = index > 0 ? sorted[index - 1] : null;
  const next = index < sorted.length - 1 ? sorted[index + 1] : null;
  const related = liveSessions.filter((item) => item.category === session.category && item.id !== session.id);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://7kchain.cn/' },
          { '@type': 'ListItem', position: 2, name: '今日直播排期', item: 'https://7kchain.cn/schedule' },
          {
            '@type': 'ListItem',
            position: 3,
            name: session.title,
            item: `https://7kchain.cn/live/${session.id}`,
          },
        ],
      },
      {
        '@type': 'BroadcastEvent',
        name: session.title,
        description: session.summary,
        startDate: buildIso(session.start),
        endDate: buildIso(session.end),
        isLiveBroadcast: session.status === 'live',
        videoFormat: 'Live',
        inLanguage: 'zh-CN',
        performer: { '@type': 'Person', name: session.host },
        url: `https://7kchain.cn/live/${session.id}`,
      },
    ],
  };

  return (
    <SiteLayout>
      <SeoHead
        title={`${session.title} - 爆料直播网直播场次详情`}
        description={`${session.summary} 场次时间 ${session.start}-${session.end}，主播 ${session.host}，栏目 ${session.category}。`}
        keywords={`${session.title},${session.category},爆料直播,今日直播排期,${session.host}`}
        path={`/live/${session.id}`}
        type="article"
        jsonLd={jsonLd}
      />

      <section className="w-full border-b border-background-200 bg-background-100">
        <div className="container-page py-6 md:py-8">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: '今日排期', path: '/schedule' },
              { label: session.category },
            ]}
          />
        </div>
      </section>

      <section className="w-full bg-background-50 py-10 md:py-14">
        <div className="container-page grid grid-cols-1 gap-10 lg:grid-cols-[1fr_340px] lg:gap-12">
          <article>
            <div className="overflow-hidden rounded-lg border border-background-200">
              <div className="relative w-full bg-background-200">
                <span className="block aspect-[16/9] w-full">
                  <img
                    src={session.cover}
                    alt={`爆料直播网 - ${session.title} 直播封面`}
                    title={`${session.title} 爆料直播 直播场次详情`}
                    className="h-full w-full object-cover object-top"
                  />
                </span>
                <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-foreground-950/10 to-transparent" />
                <span
                  className={`absolute left-4 top-4 flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium ${
                    session.status === 'live'
                      ? 'bg-accent-500 text-background-50'
                      : session.status === 'upcoming'
                        ? 'bg-background-50/90 text-foreground-900'
                        : 'bg-foreground-950/70 text-background-100'
                  }`}
                >
                  {session.status === 'live' && (
                    <span className="h-1.5 w-1.5 rounded-full bg-background-50 animate-pulse" />
                  )}
                  {statusText[session.status]}
                </span>
                <div className="absolute inset-x-0 bottom-0 p-4 md:p-6">
                  <p className="flex flex-wrap items-center gap-2 text-xs text-background-200">
                    <span className="rounded bg-secondary-500 px-2 py-0.5 font-medium text-background-50">
                      {session.category}
                    </span>
                    <span>
                      {session.start} - {session.end}
                    </span>
                    {session.status !== 'upcoming' && session.viewers > 0 && (
                      <span>· {formatCount(session.viewers)} 人在看</span>
                    )}
                  </p>
                </div>
              </div>
            </div>

            <h1 className="mt-6 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-[32px]">
              {session.title}
            </h1>

            <div className="mt-4 flex flex-wrap items-center gap-3 text-sm text-foreground-600">
              <span className="flex items-center gap-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-100 text-xs font-semibold text-primary-800">
                  {session.host.slice(0, 1)}
                </span>
                主播 {session.host}
              </span>
              <span className="text-foreground-300">·</span>
              <span className="flex items-center gap-1">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-fire-line" aria-hidden="true" />
                </span>
                热度 {formatCount(session.hot)}
              </span>
              <span className="text-foreground-300">·</span>
              <span className="flex flex-wrap gap-2">
                {session.tags.map((tag) => (
                  <span key={tag} className="rounded border border-background-300 px-2 py-0.5 text-xs text-foreground-600">
                    {tag}
                  </span>
                ))}
              </span>
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-3 border-y border-background-200 py-4">
              {session.status === 'upcoming' && (
                <CountdownTimer targetMs={toNextTimestamp(session.start)} tone="primary" />
              )}
              {session.status !== 'ended' ? (
                <>
                  <Link
                    to="/schedule"
                    className="flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                  >
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-live-line" aria-hidden="true" />
                    </span>
                    进入直播间
                  </Link>
                  <ReminderButton sessionId={session.id} />
                </>
              ) : (
                <Link
                  to="/replay"
                  className="flex items-center gap-1.5 whitespace-nowrap rounded-md bg-secondary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-secondary-600"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-history-line" aria-hidden="true" />
                  </span>
                  查看本场回放
                </Link>
              )}
            </div>

            <div className="mt-8">
              <h2 className="font-heading text-lg font-bold text-foreground-950">本场看点</h2>
              <ul className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
                {session.highlights.map((item, idx) => (
                  <li key={item} className="rounded-lg border border-background-200 bg-background-50 p-4">
                    <span className="font-heading text-xs font-bold text-accent-600">
                      看点 {pad(idx + 1)}
                    </span>
                    <p className="mt-2 text-sm leading-relaxed text-foreground-700">{item}</p>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-8">
              <h2 className="font-heading text-lg font-bold text-foreground-950">场次说明</h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">{session.summary}</p>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                本场安排在 {session.start} 开始，预计 {session.end} 结束，栏目为「{session.category}」，
                主播 {session.host} 会在场次中围绕上述看点展开。页面展示的时间与状态会随实际情况自动更新，
                若遇临时调整以主播开播通知为准。
              </p>
            </div>

            <nav className="mt-10 flex flex-col gap-3 border-t border-background-200 pt-6 sm:flex-row sm:items-center sm:justify-between">
              {prev ? (
                <Link
                  to={`/live/${prev.id}`}
                  className="group flex max-w-full items-center gap-2 text-sm text-foreground-700 transition-colors hover:text-primary-700"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-left-line" aria-hidden="true" />
                  </span>
                  <span className="truncate">上一场：{prev.title}</span>
                </Link>
              ) : (
                <span className="text-sm text-foreground-400">已是当天第一场</span>
              )}
              {next ? (
                <Link
                  to={`/live/${next.id}`}
                  className="group flex max-w-full items-center gap-2 text-sm text-foreground-700 transition-colors hover:text-primary-700 sm:justify-end"
                >
                  <span className="truncate">下一场：{next.title}</span>
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line" aria-hidden="true" />
                  </span>
                </Link>
              ) : (
                <span className="text-sm text-foreground-400">已是当天最后一场</span>
              )}
            </nav>
          </article>

          <aside className="flex flex-col gap-6 lg:sticky lg:top-24 lg:self-start">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">主播信息</h2>
              <div className="mt-4 flex items-center gap-3">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-500 font-heading text-lg font-bold text-background-50">
                  {session.host.slice(0, 1)}
                </span>
                <div>
                  <p className="font-heading text-sm font-bold text-foreground-950">{session.host}</p>
                  <p className="mt-0.5 text-xs text-foreground-500">主要栏目：{session.category}</p>
                </div>
              </div>
              <p className="mt-4 text-xs leading-relaxed text-foreground-600">
                长期在该栏目固定开播，场次以内容梳理与实时互动为主，回放通常会在结束后 2 小时内整理上线。
              </p>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-100 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">同栏目推荐</h2>
              {related.length > 0 ? (
                <ul className="mt-4 flex flex-col gap-4">
                  {related.slice(0, 4).map((item) => (
                    <li key={item.id} className="flex gap-3 border-b border-background-200 pb-4 last:border-0 last:pb-0">
                      <Link
                        to={`/live/${item.id}`}
                        className="block h-[52px] w-[88px] shrink-0 overflow-hidden rounded-md bg-background-200"
                      >
                        <img
                          src={item.cover}
                          alt={`爆料直播网 - ${item.title} 直播封面`}
                          title={`${item.title} 同栏目直播推荐`}
                          className="h-full w-full object-cover object-top"
                        />
                      </Link>
                      <div className="min-w-0">
                        <Link
                          to={`/live/${item.id}`}
                          className="line-clamp-2 font-heading text-[13px] font-semibold leading-snug text-foreground-950 transition-colors hover:text-primary-700"
                        >
                          {item.title}
                        </Link>
                        <p className="mt-1 text-[11px] text-foreground-500">
                          {item.start} · {statusText[item.status]}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-3 text-xs text-foreground-500">该栏目今天只有这一场，欢迎去其他栏目看看。</p>
              )}
              <Link
                to="/schedule"
                className="mt-4 flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-700"
              >
                返回完整排期
              </Link>
            </div>
          </aside>
        </div>
      </section>
    </SiteLayout>
  );
}